import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { api } from "../api/client";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  ListSkeleton,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
} from "../components";
import { colors } from "../theme";

/**
 * Merchant-team management — one team per merchant account.
 *
 * Every teammate gets access to every vendor on the merchant. Adding
 * a member grants access to ALL vendors in one shot; removing strips
 * them from all. The teammate just needs to sign in (Google, phone, etc.)
 * with the same email once and the merchant appears in their dashboard.
 */
function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [emailInput, setEmailInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [pendingRemove, setPendingRemove] = useState(null); // member obj
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMerchantTeam();
      setMembers(res?.data?.members ?? []);
    } catch (e) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const email = emailInput.trim();
    if (!email) return;
    setAdding(true);
    setAddError(null);
    try {
      await api.addMerchantTeammate(email);
      setEmailInput("");
      await fetchTeam();
    } catch (err) {
      // Backend AppError messages come through verbatim as response text.
      setAddError(err?.message ?? String(err));
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.removeMerchantTeammate(pendingRemove.userId);
      setPendingRemove(null);
      await fetchTeam();
    } catch (err) {
      setRemoveError(err?.message ?? String(err));
    } finally {
      setRemoving(false);
    }
  };

  const initials = (m) => {
    const f = (m.firstName ?? "").trim();
    const l = (m.lastName ?? "").trim();
    if (f || l) return `${f[0] ?? ""}${l[0] ?? ""}`.toUpperCase();
    const e = (m.email ?? "").trim();
    return e ? e[0].toUpperCase() : "?";
  };

  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) => {
        if (a.isSelf && !b.isSelf) return -1;
        if (!a.isSelf && b.isSelf) return 1;
        return (a.assignedAt ?? "").localeCompare(b.assignedAt ?? "");
      }),
    [members],
  );

  const memberCountChip =
    !loading && !error ? (
      <Chip
        size="small"
        label={`${members.length} ${members.length === 1 ? "member" : "members"}`}
      />
    ) : null;

  return (
    <PageContainer maxWidth="md">
      <PageHeader
        title="Team"
        subtitle="People who can manage every vendor on this merchant."
      />

      <Stack spacing={3}>
        <SectionCard
          title="Add teammate"
          subtitle="Enter the teammate's email. They get access to every vendor on this merchant immediately — once they sign in (Google, phone, etc.) using that same email, the merchant shows up in their dashboard."
        >
          <Box
            component="form"
            onSubmit={handleAdd}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "stretch",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                if (addError) setAddError(null);
              }}
              placeholder="teammate@email.com"
              size="small"
              fullWidth
              disabled={adding}
              type="email"
              autoComplete="off"
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={
                adding ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                )
              }
              disabled={adding || !emailInput.trim()}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Add teammate
            </Button>
          </Box>
          {addError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setAddError(null)}
            >
              {addError}
            </Alert>
          )}
        </SectionCard>

        <SectionCard title="Members" actions={memberCountChip} disablePadding>
          {loading ? (
            <ListSkeleton rows={3} />
          ) : error ? (
            <ErrorState
              title="Couldn't load the team"
              description={error}
              onRetry={fetchTeam}
            />
          ) : sortedMembers.length === 0 ? (
            <EmptyState
              icon={<GroupOutlinedIcon sx={{ fontSize: 32 }} />}
              title="No teammates yet"
              description="Add a teammate above to give them access to every vendor on this merchant."
              dense
            />
          ) : (
            <List disablePadding>
              {sortedMembers.map((m, idx) => (
                <ListItem
                  key={m.userId}
                  divider={idx < sortedMembers.length - 1}
                  sx={{ py: 1.5, px: { xs: 2, sm: 2.5 } }}
                  secondaryAction={
                    !m.isSelf ? (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setRemoveError(null);
                          setPendingRemove(m);
                        }}
                        title="Remove teammate"
                        aria-label={`Remove ${m.email}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: colors.brand.purpleTint,
                        color: colors.brand.purple,
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {initials(m)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography variant="body1" fontWeight={600}>
                          {[m.firstName, m.lastName]
                            .filter(Boolean)
                            .join(" ") || "(no name)"}
                        </Typography>
                        {m.isSelf && <StatusChip tone="brand" label="You" />}
                        {m.hasGoogleLinked ? (
                          <StatusChip tone="success" label="Signed in" />
                        ) : (
                          <StatusChip
                            tone="warning"
                            label="Awaiting first sign-in"
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          {m.email ?? "(no email)"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="span"
                        >
                          Added{" "}
                          {m.assignedAt
                            ? new Date(m.assignedAt).toLocaleDateString()
                            : "—"}
                        </Typography>
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </SectionCard>
      </Stack>

      <ConfirmDialog
        open={Boolean(pendingRemove)}
        onClose={() => setPendingRemove(null)}
        onConfirm={handleConfirmRemove}
        submitting={removing}
        error={removeError}
        title="Remove teammate?"
        description={
          (pendingRemove?.email ?? "This teammate") +
          " will lose access to every vendor on this merchant immediately. Their PABS account stays intact — they just won't see the merchant anymore."
        }
        confirmLabel="Remove"
        confirmColor="error"
      />
    </PageContainer>
  );
}

export default TeamPage;
